import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { LIFECYCLE_PRODUCT_LABELS } from '../journey-workspace/journeyLifecycleModel';
import {
  JOURNEY_MEMBER_ROLE_PRODUCT_LABELS,
  MY_JOURNEYS_PRODUCT_CHROME,
} from '../journey-workspace/journeyWorkspacePresentation';

/** Same product labels as Overview lifecycle (UX-03-A / UX-33). */
const STATUS_LABELS = LIFECYCLE_PRODUCT_LABELS;

const ROLE_LABELS = JOURNEY_MEMBER_ROLE_PRODUCT_LABELS;

const pad2 = (value) => String(value).padStart(2, '0');

/** Coerce Jackson ISO string or numeric LocalDate array into YYYY-MM-DD. */
export function coerceLocalDate(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : value;
  }
  if (Array.isArray(value) && value.length >= 3) {
    return `${value[0]}-${pad2(value[1])}-${pad2(value[2])}`;
  }
  return null;
}

/** Coerce Jackson ISO string or numeric LocalDateTime array into a stable display string. */
export function coerceDateTime(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'string') return value;
  if (Array.isArray(value) && value.length >= 3) {
    const date = `${value[0]}-${pad2(value[1])}-${pad2(value[2])}`;
    if (value.length < 5) return date;
    const time = `${pad2(value[3] || 0)}:${pad2(value[4] || 0)}:${pad2(value[5] || 0)}`;
    return `${date}T${time}`;
  }
  return null;
}

export function parseDestinationSummary(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try {
    const value = JSON.parse(raw);
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
}

export function destinationLabel(raw) {
  const value = parseDestinationSummary(raw);
  return value.displayName || value.name || value.summary || value.city || '';
}

export function encodeDestinationSummary(text) {
  const trimmed = String(text || '').trim();
  if (!trimmed) return null;
  return JSON.stringify({ displayName: trimmed });
}

export function lifecycleStatusLabel(status) {
  if (!status) return '';
  return STATUS_LABELS[status] || String(status);
}

export function memberRoleLabel(role) {
  if (!role) return '';
  return ROLE_LABELS[role] || String(role);
}

export function formatJourneyDateRange(startDate, endDate) {
  const start = coerceLocalDate(startDate);
  const end = coerceLocalDate(endDate);
  if (!start && !end) return '';
  if (start && end && start !== end) return `${start} – ${end}`;
  return start || end || '';
}

export function normalizeMyJourneySummary(value) {
  const startDate = coerceLocalDate(value?.startDate);
  const endDate = coerceLocalDate(value?.endDate);
  return {
    journeyId: value?.journeyId,
    title: value?.title || MY_JOURNEYS_PRODUCT_CHROME.untitled,
    destination: destinationLabel(value?.destinationSummaryJson),
    startDate,
    endDate,
    dateRange: formatJourneyDateRange(startDate, endDate),
    lifecycleStatus: value?.lifecycleStatus || '',
    lifecycleLabel: lifecycleStatusLabel(value?.lifecycleStatus),
    coverUrl: resolveMemberMediaUrl(value?.coverFileReference),
    requesterRole: value?.requesterRole || '',
    requesterRoleLabel: memberRoleLabel(value?.requesterRole),
    requesterMembershipStatus: value?.requesterMembershipStatus || '',
    ownerMemberId: value?.ownerMemberId ?? null,
    rowVersion: value?.rowVersion ?? null,
    updateTime: coerceDateTime(value?.updateTime),
  };
}

export function normalizeMyJourneyList(payload) {
  if (!Array.isArray(payload)) return [];
  return payload.map(normalizeMyJourneySummary);
}
